import * as chrono from "chrono-node";
import type { Due } from "./entities.js";

/**
 * Parses Todoist-style date text out of quick-add input.
 * Recurrence strings ("every monday", "every! 3 days") are detected and kept verbatim;
 * their occurrences are computed by `nextOccurrence` / `advanceRecurring`, which the
 * server-side recurrence engine also uses.
 */

const RECURRING = /\b(every!?|daily|weekly|monthly|yearly|annually|weekdays?|workdays?)\b/i;

function pad(n: number) { return String(n).padStart(2, "0"); }
export function toISODate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export interface ParsedQuickAdd {
  content: string;
  due: Due | null;
  priority: 1 | 2 | 3 | 4 | null;
  labels: string[];
  projectName: string | null;
}

export function parseQuickAdd(
  input: string,
  now: Date = new Date(),
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): ParsedQuickAdd {
  let text = input.trim();
  let priority: ParsedQuickAdd["priority"] = null;
  const labels: string[] = [];
  let projectName: string | null = null;

  text = text.replace(/(^|\s)p([1-4])\b/i, (_m, lead: string, p: string) => { priority = (5 - Number(p)) as 1 | 2 | 3 | 4; return lead; });
  text = text.replace(/(^|\s)@([\w-]+)/g, (_m, lead: string, l: string) => { labels.push(l); return lead; });
  text = text.replace(/(^|\s)#([\w-]+)/, (_m, lead: string, p: string) => { projectName = p; return lead; });

  const due = parseDue(text, now, timezone);
  if (due) text = due.remaining;

  return { content: text.replace(/\s+/g, " ").trim(), due: due?.due ?? null, priority, labels, projectName };
}

export type QuickAddTokenKind = "date" | "priority" | "label" | "project";
export interface QuickAddToken { kind: QuickAddTokenKind; start: number; end: number; text: string }

/**
 * Where each recognised token sits in the raw quick-add text, for inline highlighting
 * and removable chips. Mirrors parseQuickAdd: first p1-p4, every @label, first #project,
 * then the date phrase in what is left. Offsets index into `input` as typed.
 */
export function quickAddTokens(input: string, now: Date = new Date()): QuickAddToken[] {
  const tokens: QuickAddToken[] = [];
  const mask = (start: number, end: number) => { masked = masked.slice(0, start) + " ".repeat(end - start) + masked.slice(end); };
  let masked = input;
  const take = (kind: QuickAddTokenKind, re: RegExp, all = false) => {
    const rx = new RegExp(re.source, all ? "gi" : "i");
    let m: RegExpExecArray | null;
    while ((m = rx.exec(masked))) {
      const start = m.index + m[1]!.length;
      const end = m.index + m[0].length;
      tokens.push({ kind, start, end, text: input.slice(start, end) });
      if (!all) break;
    }
    for (const t of tokens.filter((x) => x.kind === kind)) mask(t.start, t.end);
  };
  take("priority", /(^|\s)p[1-4]\b/);
  take("label", /(^|\s)@[\w-]+/, true);
  take("project", /(^|\s)#[\w-]+/);
  const recurring = RECURRING.exec(masked);
  if (recurring) {
    const end = masked.trimEnd().length;
    if (end > recurring.index) tokens.push({ kind: "date", start: recurring.index, end, text: input.slice(recurring.index, end) });
  } else {
    const r = chrono.en.GB.parse(masked, now, { forwardDate: true })[0];
    if (r) tokens.push({ kind: "date", start: r.index, end: r.index + r.text.length, text: input.slice(r.index, r.index + r.text.length) });
  }
  return tokens.sort((a, b) => a.start - b.start);
}

/** Removes one token from quick-add text, tidying the spaces around it. */
export function removeQuickAddToken(input: string, token: Pick<QuickAddToken, "start" | "end">): string {
  return (input.slice(0, token.start).replace(/\s+$/, "") + " " + input.slice(token.end).replace(/^\s+/, "")).trim();
}

export function parseDue(text: string, now: Date = new Date(), timezone?: string): { due: Due; remaining: string } | null {
  const recurring = RECURRING.exec(text);
  if (recurring) {
    const start = recurring.index;
    const phrase = text.slice(start).trim();
    const next = nextOccurrence(phrase, now);
    const due: Due = {
      date: toISODate(next.date),
      datetime: next.hasTime ? next.date.toISOString() : null,
      timezone: next.hasTime ? timezone ?? null : null,
      string: phrase,
      isRecurring: true,
    };
    return { due, remaining: text.slice(0, start) };
  }

  const results = chrono.en.GB.parse(text, now, { forwardDate: true });
  const r = results[0];
  if (!r) return null;
  const date = r.start.date();
  const hasTime = r.start.isCertain("hour");
  const due: Due = {
    date: toISODate(date),
    datetime: hasTime ? date.toISOString() : null,
    timezone: hasTime ? timezone ?? null : null,
    string: r.text,
    isRecurring: false,
  };
  const remaining = (text.slice(0, r.index) + text.slice(r.index + r.text.length)).replace(/\s+(at|on|by)\s*$/i, "");
  return { due, remaining };
}

/** Builds a one-off Due from a calendar date (YYYY-MM-DD) and an optional local time (HH:MM). */
export function dueFromDateTime(
  date: string,
  time: string | null,
  timezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): Due {
  const label = new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  if (!time) return { date, datetime: null, timezone: null, string: label, isRecurring: false };
  const dt = new Date(`${date}T${time}:00`);
  return { date, datetime: dt.toISOString(), timezone, string: `${label} ${time}`, isRecurring: false };
}

// ---------------------------------------------------------------------------
// Recurrence rules
// ---------------------------------------------------------------------------

const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
type Unit = "day" | "week" | "month" | "year";

export type RecurrenceRule =
  | { kind: "weekdays"; days: number[]; hour: number; minute: number; hasTime: boolean; fromCompletion: boolean }
  | { kind: "monthday"; day: number; hour: number; minute: number; hasTime: boolean; fromCompletion: boolean }
  | { kind: "interval"; n: number; unit: Unit; hour: number; minute: number; hasTime: boolean; fromCompletion: boolean };

/**
 * Parses a recurrence phrase. Supports: every day / daily, every N days|weeks|months|years,
 * every weekday|workday, every <weekday>[, <weekday>...], weekly/monthly/yearly,
 * every Nth (day of month), each with an optional "at <time>" suffix. "every!" means
 * the next instance counts from completion rather than from the previous due date.
 */
export function parseRecurrence(phrase: string): RecurrenceRule {
  const fromCompletion = /^\s*every!/i.test(phrase);
  const lower = phrase.toLowerCase().replace(/^\s*every!?\s*/, "");
  let hasTime = false;
  let hour = 0;
  let minute = 0;
  const timeMatch = /\bat\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/.exec(lower);
  if (timeMatch) {
    hasTime = true;
    hour = Number(timeMatch[1]);
    minute = Number(timeMatch[2] ?? 0);
    if (timeMatch[3] === "pm" && hour < 12) hour += 12;
    if (timeMatch[3] === "am" && hour === 12) hour = 0;
  }
  const body = lower.replace(/\bat\s+.*$/, "").trim();
  const common = { hour, minute, hasTime, fromCompletion };

  const days = WEEKDAYS.map((w, i) => (new RegExp(`\\b${w.slice(0, 3)}`).test(body) ? i : -1)).filter((i) => i >= 0);
  if (/weekday|workday/.test(body)) days.push(1, 2, 3, 4, 5);
  if (days.length) return { kind: "weekdays", days: [...new Set(days)].sort(), ...common };

  const nth = /(\d{1,2})(st|nd|rd|th)?\b(?!\s*(day|week|month|year))/.exec(body);
  if (/month/.test(body) && nth) return { kind: "monthday", day: Number(nth[1]), ...common };

  const explicit = /(\d+)?\s*(day|week|month|year)s?/.exec(body);
  if (explicit) return { kind: "interval", n: Number(explicit[1] ?? 1), unit: explicit[2] as Unit, ...common };
  if (/weekly/.test(body)) return { kind: "interval", n: 1, unit: "week", ...common };
  if (/monthly/.test(body)) return { kind: "interval", n: 1, unit: "month", ...common };
  if (/yearly|annually/.test(body)) return { kind: "interval", n: 1, unit: "year", ...common };
  return { kind: "interval", n: 1, unit: "day", ...common };
}

function withTime(rule: RecurrenceRule, d: Date): Date {
  const c = new Date(d);
  c.setHours(rule.hour, rule.minute, 0, 0);
  return c;
}

function addInterval(d: Date, n: number, unit: Unit): Date {
  const r = new Date(d);
  if (unit === "day") r.setDate(r.getDate() + n);
  else if (unit === "week") r.setDate(r.getDate() + 7 * n);
  else if (unit === "month") r.setMonth(r.getMonth() + n);
  else r.setFullYear(r.getFullYear() + n);
  return r;
}

/** First occurrence of the rule on or after `from` (strictly after when the rule has a time). */
export function nextOccurrence(phrase: string | RecurrenceRule, from: Date): { date: Date; hasTime: boolean } {
  const rule = typeof phrase === "string" ? parseRecurrence(phrase) : phrase;
  const base = new Date(from);
  base.setSeconds(0, 0);
  const startDay = new Date(base);
  startDay.setHours(0, 0, 0, 0);
  const ok = (d: Date) => (rule.hasTime ? d.getTime() > base.getTime() : d.getTime() >= startDay.getTime());

  if (rule.kind === "weekdays") {
    for (let i = 0; i < 8; i++) {
      const d = new Date(startDay);
      d.setDate(d.getDate() + i);
      if (rule.days.includes(d.getDay()) && ok(withTime(rule, d))) return { date: withTime(rule, d), hasTime: rule.hasTime };
    }
  }
  if (rule.kind === "monthday") {
    for (let m = 0; m < 12; m++) {
      const d = new Date(startDay.getFullYear(), startDay.getMonth() + m, rule.day);
      if (d.getDate() === rule.day && ok(withTime(rule, d))) return { date: withTime(rule, d), hasTime: rule.hasTime };
    }
  }
  // Interval rules have no fixed anchor on first use: start today (or the next slot if the time has passed).
  const today = withTime(rule, startDay);
  if (ok(today)) return { date: today, hasTime: rule.hasTime };
  const n = rule.kind === "interval" ? rule.n : 1;
  const unit: Unit = rule.kind === "interval" ? rule.unit : "day";
  return { date: addInterval(today, n, unit), hasTime: rule.hasTime };
}

/** Given the due a recurring task was completed with, returns the due for the next instance. */
export function advanceRecurring(due: Due, completedAt: Date = new Date()): Due {
  const rule = parseRecurrence(due.string);
  const previous = new Date(due.datetime ?? `${due.date}T00:00:00`);
  let next: Date;

  if (rule.kind === "interval") {
    // Anchor on completion for "every!", otherwise on the previous due date.
    const anchorDay = rule.fromCompletion ? completedAt : previous;
    const anchor = withTime(rule, anchorDay);
    next = addInterval(anchor, rule.n, rule.unit);
  } else {
    // Calendar-anchored rules: the first slot strictly after the anchor day.
    const anchorDay = new Date(rule.fromCompletion ? completedAt : previous);
    anchorDay.setDate(anchorDay.getDate() + 1);
    anchorDay.setHours(0, 0, 0, 0);
    next = nextOccurrence(rule, anchorDay).date;
  }
  return { ...due, date: toISODate(next), datetime: rule.hasTime ? next.toISOString() : null };
}
